import { twMerge } from "tailwind-merge";
import type { HTMLAttributes } from "react";
import { Archive } from "lucide-react";

export interface CompactingIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  text?: string;
  className?: string;
}

/**
 * Shown while the agent is compacting its conversation history (summarizing
 * older turns to free up context). Mirrors ThinkingIndicator's shape so it
 * slots into the same place in a chat message list. Driven by the streamed
 * `isCompacting` flag — see `useIsCompacting()`.
 */
export function CompactingIndicator({
  text = "Summarizing conversation",
  className,
  ...props
}: CompactingIndicatorProps) {
  return (
    <div
      role="status"
      data-testid="compacting-indicator"
      aria-label={text}
      className={twMerge("flex items-center gap-2", className)}
      {...props}
    >
      <div className="relative h-[34px] w-[34px]">
        {/* Spinning progress ring */}
        <svg
          className="absolute inset-0 h-full w-full animate-spin"
          viewBox="0 0 34 34"
          fill="none"
        >
          <circle cx="17" cy="17" r="15" stroke="#E2E1E0" strokeWidth="2" strokeLinecap="round" />
          <circle
            cx="17"
            cy="17"
            r="15"
            stroke="#3748B8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="94.2"
            strokeDashoffset="70"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Archive className="h-[16px] w-[16px] text-primary-700" strokeWidth={1.5} />
        </div>
      </div>
      <span className="text-sm italic text-base-400">{text}...</span>
    </div>
  );
}
